import { debug } from '@bt-react/libs/Logger'
import cookie from '@bt-react/libs/cookie'
import { ThunkAction } from 'redux-thunk'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { State, ReduxAction } from '../../../dataStore/reducers/reducerType'
import { CommunicationType, CommunicationRequest, UnmarshalResponse } from '../../../services/apiGateway/apiGatewayTypes'
import selectWebcastTimezone from '../../selectors/selectWebcastTimezone'
import apiGateway from '../../../services/apiGateway'

export interface CommunicationsSlice {
  communications: CommunicationType[]
  searchQuery: string
  total: number
  pageSize: number
  pageSizeSelected: number
}

export const communicationsDefaultState: CommunicationsSlice = {
  total: 0,
  communications: [],
  searchQuery: '',
  pageSize: 15,
  pageSizeSelected: 50
}

// Takes the current list of communications and adds the next n to the communications array

export const parseRange = (list: UnmarshalResponse['communications'], range: CommunicationType[], startIndex: number) => {

  if (range) {

    let listPointer = startIndex

    while (listPointer < (startIndex + range.length)) {

      list[listPointer] = range[ listPointer - startIndex ]

      listPointer++

    }

  }

  return list

}

const communicationsSlice = createSlice({
  name: 'communications',
  initialState: communicationsDefaultState,
  reducers: {
    storeCommunications (state, { payload }: PayloadAction<{ data: UnmarshalResponse, startIndex: number, eraseOld: boolean }>) {

      state.communications = payload.eraseOld ? payload.data.communications : parseRange(state.communications, payload.data.communications, payload.startIndex)
      state.total = payload.data.total || 0

    },
    saveSearchQuery (state, { payload }: PayloadAction<string>) {

      state.searchQuery = payload

    }
  }
})

export const fetchChannelCommunications = ({ startIndex = 0, eraseOld, promiseResolver }: { startIndex: number, eraseOld: boolean, promiseResolver?: any }): ThunkAction<void, State, unknown, ReduxAction> => async (dispatch, getState: () => State) => {

  const { channelId } = getState().config
  const { pageSize, searchQuery } = getState().communications
  const { timeZone } = getState().user.data

  if (!channelId) return

  const data: CommunicationRequest = {

    channel: {
      id: channelId
    },
    offset: startIndex,
    limit: pageSize,
    status: 'recorded',
    providers: 'brighttalkhd, webcamtalk, video',
    sort: {
      order: 'desc',
      name: 'scheduled'
    },
    visibilities: 'private, public',
    syndication: {
      authorisation: 'approved'
    }

  }

  if (searchQuery) {

    data.phrase = searchQuery

  }

  let response: UnmarshalResponse
  const timezone = selectWebcastTimezone(getState()) || timeZone

  try {

    response = await apiGateway.fetchCommunications(data, timezone || '')

  }
  catch (e) {

    debug('Communications fetch failed', e.message)
    return

  }

  dispatch(communicationsSlice.actions.storeCommunications({ data: response, startIndex, eraseOld }))
  if (promiseResolver) promiseResolver()

}

export const searchChannelCommunications = (searchQuery) => async (dispatch) => {

  dispatch(communicationsSlice.actions.saveSearchQuery(searchQuery))
  dispatch(fetchChannelCommunications({ startIndex: 0, eraseOld: true }))

}

export default communicationsSlice
